import React, { RefObject, ReactElement, forwardRef, Ref } from "react";
import cx from "classnames";
import { useTranslation } from "i18n";
import { useAuth } from "shared-auth";
import { dataTestIds } from "data-testids";
import { ReactComponent as OpenMenuIcon } from "../../../../../images/svg/icons/action-burger.svg";
import { ReactComponent as CloseMenuIcon } from "../../../../../images/svg/icons/action-close-menu.svg";
import { ReactComponent as CloseMenuMobileIcon } from "../../../../../images/svg/icons/cross-thin.svg";
import { ReactComponent as SpotlightLogoWhiteSvg } from "../../../../../../assets/svg/Spotlight-Logo-white.svg";
import { myAccountLink, focusStyle } from "../../constants";
import { useIsMobile } from "../../../../../generics/dom-extensions";
import Avatar from "../Avatar";
import { Button } from "../../../../Atoms/Button/Button";
import SkipToContentButton from "../SkipToContentButton";
import TestPill from "./TestPill";

interface Props {
  isLhnVisible: boolean;
  toggleLhn: () => void;
  toggleUserMenu: () => void;
  userMenuButtonRef: RefObject<HTMLButtonElement>;
  dynamicHeaderContent?: React.ReactNode;
  avatar?: string;
  serviceBannerPortalNone?: ReactElement;
}

export const openIconTestId = "open-icon-test-id";

export const closeIconTestId = "close-icon-test-id";

const Header = forwardRef(
  (
    {
      isLhnVisible,
      toggleLhn,
      toggleUserMenu,
      dynamicHeaderContent,
      userMenuButtonRef,
      avatar,
      serviceBannerPortalNone,
    }: Props,
    ref: Ref<HTMLDivElement>
  ) => {
    const { t } = useTranslation();
    const auth = useAuth();
    const isMobile = useIsMobile();

    const iconBaseClass =
      "opacity-0 transition-opacity duration-300 ease-in-out absolute";
    const closeIconClass = cx(iconBaseClass, { "opacity-100": isLhnVisible });

    return (
      <div className="sticky top-0 z-10" ref={ref}>
        {serviceBannerPortalNone}
        <header
          className={cx(
            "flex h-auto items-center px-0.5 py-[5px]",
            "bg-color-neutral-one text-white",
            {
              "flex-wrap": isMobile,
            }
          )}
        >
          <div className="mr-auto flex">
            {auth.isAuthenticated && (
              <button
                className={cx(
                  "relative flex h-[50px] w-[50px] items-center justify-center",
                  focusStyle
                )}
                onClick={toggleLhn}
                tabIndex={0}
              >
                <OpenMenuIcon
                  className={cx(iconBaseClass, {
                    "opacity-100": !isLhnVisible,
                  })}
                  data-testid={openIconTestId}
                />
                {isMobile ? (
                  <CloseMenuMobileIcon
                    data-testid={closeIconTestId}
                    className={closeIconClass}
                  />
                ) : (
                  <CloseMenuIcon
                    data-testid={closeIconTestId}
                    className={closeIconClass}
                  />
                )}
              </button>
            )}

            <a
              href={
                auth.isAuthenticated
                  ? myAccountLink
                  : "https://www.spotlight.com/"
              }
              className={cx(
                "ml-1.5 flex h-[50px] items-center px-2",
                focusStyle
              )}
            >
              <SpotlightLogoWhiteSvg height={30} />
              <TestPill />
            </a>

            <SkipToContentButton />
          </div>

          {auth.isAuthenticated && (
            <>
              {dynamicHeaderContent}
              <div
                className={cx(
                  "border-grey-shark-5 ml-5 flex items-center border-l px-2"
                )}
              >
                <button
                  onClick={toggleUserMenu}
                  className={cx("h-10 w-10 p-1", focusStyle)}
                  ref={userMenuButtonRef}
                  data-testid={
                    dataTestIds.componentLibrary[
                      "Organisms.NavigationHeader.Avatar.avatarIcon"
                    ]
                  }
                >
                  <Avatar
                    className="hover:border-color-accent-one"
                    img={avatar}
                  />
                </button>
              </div>
            </>
          )}

          {!auth.isAuthenticated && (
            <div className="ml-auto flex gap-2.5 whitespace-nowrap pr-[15px]">
              <Button
                type="secondary"
                href="https://www.spotlight.com/join-us"
                text={t("common:nav.joinNow")}
              />
              <Button
                type="primary"
                onClick={() => auth.signinRedirect()}
                text={t("common:nav.signin")}
              />
            </div>
          )}
        </header>
      </div>
    );
  }
);

export default Header;
